import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';

import {Markets} from 'nutshell-core/markets';
import {Session} from 'nutshell-core/session';
import type {Market, ProductId} from 'nutshell-core/types';

import {OptionWithSubtitleAndSublabel} from 'shells/select-option-components/option-with-subtitle-and-sublabel';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import type {FooterButtonProps} from 'shells/select-picker-generic/select-picker-generic';
import type {SelectSize} from 'shells/select-picker-generic/select-picker-generic-creatable';

import {appHistory} from '../../../history';

import type {ProductPickerProduct} from './graphql/helpers/product-picker-product';
import {ProductSearchExclusionLimitError} from './graphql/hooks/use-search-products';
import {MIN_PRODUCT_SEARCH_LENGTH, useSearchProducts} from './graphql/hooks/use-search-products';
import {useProductPickerSelection} from './hooks/use-product-picker-selection';
import {getProductOptions} from './product-picker-options';
import type {ProductOption, ProductOptionsContext} from './product-picker-options';

export type {ProductOption} from './product-picker-options';

interface Props {
    onChange: (option: ProductOption) => void;
    onSelectedProductLoad?: (product: ProductPickerProduct | null) => void;
    selectedId: string | null | undefined;
    size?: SelectSize;
    hideDivider?: boolean;
    defaultMenuIsOpen?: boolean;
    placeholder?: string;
    currentMarket?: Market | null | undefined;
    /** Product relationship filters search the whole catalog, regardless of market pricing. */
    shouldFilterByMarket?: boolean;
    excludeProductIds?: ProductId[];
    autoFocus?: boolean;
    inputId?: string;
    isDisabled?: boolean;
    openMenuOnFocus?: boolean;
    onBlur?: () => void;
    onClear?: () => void;
    getRef?: (ref?: any) => void;
    footerButton?: FooterButtonProps;
    maxMenuHeight?: number;
    noOptionsMessage?: (input?: {inputValue?: string}) => string;
    menuZIndex?: number;
    menuPortalTarget?: HTMLElement;
    menuPlacement?: 'auto' | 'bottom' | 'top';
}

function reportProductSearchError(error: unknown): void {
    const windowWithTrackJs = window as Window & {
        trackJs?: {track: (trackedError: unknown) => void};
    };
    windowWithTrackJs.trackJs?.track(error);
}

function getDefaultNoOptionsMessage({inputValue = ''}: {inputValue?: string} = {}): string {
    return inputValue.trim().length < MIN_PRODUCT_SEARCH_LENGTH
        ? 'Enter at least 2 characters'
        : 'No products found';
}

export function ReduxSelectProducts(props: Props) {
    const {noOptionsMessage: customNoOptionsMessage, onChange} = props;
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    // @ts-expect-error
    const loggedInUser = useSelector((state) => Session.getSessionStateUser(state));
    const currentMarket =
        props.shouldFilterByMarket === false ? null : props.currentMarket || defaultMarket;
    const excludeProductIds = props.excludeProductIds || [];
    const omittedProductIds = excludeProductIds.filter((id) => id !== props.selectedId);
    const omittedProductIdsKey = JSON.stringify([...omittedProductIds].sort());
    const omittedProductIdsSet = useMemo(
        () => new Set(JSON.parse(omittedProductIdsKey) as ProductId[]),
        [omittedProductIdsKey]
    );
    const searchContextKey = currentMarket?.id ?? 'no-market';
    const {searchProducts, error: searchError} = useSearchProducts({
        marketId: currentMarket?.id,
        omittedProductIds,
    });
    const {
        selectedProduct,
        isLoading: isHydratingSelection,
        hasError: hasSelectionHydrationError,
        retry: retrySelectionHydration,
        selectProduct,
    } = useProductPickerSelection({
        selectedId: props.selectedId,
        marketId: currentMarket?.id,
        onSelectedProductLoad: props.onSelectedProductLoad,
    });
    const optionsContext = useRef<ProductOptionsContext>({
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    });
    optionsContext.current = {
        currentMarket,
        excludeProductIds,
        selectedId: props.selectedId,
        selectedProduct,
    };

    useEffect(() => {
        if (searchError && !(searchError instanceof ProductSearchExclusionLimitError)) {
            reportProductSearchError(searchError);
        }
    }, [searchError]);

    const loadOptions = useCallback(
        (query: string) =>
            searchProducts(query).then((products) =>
                products ? getProductOptions(products, optionsContext.current) : []
            ),
        [searchProducts]
    );

    const handleChange = useCallback(
        (option: ProductOption) => {
            if (
                option.product.priceMarketId !== (currentMarket?.id ?? null) ||
                omittedProductIdsSet.has(option.product.id)
            ) {
                return;
            }

            selectProduct(option.product);
            onChange(option);
        },
        [currentMarket?.id, omittedProductIdsSet, onChange, selectProduct]
    );

    const noOptionsMessage = useCallback(
        (input: {inputValue?: string} = {}) => {
            if (searchError) {
                return searchError instanceof ProductSearchExclusionLimitError
                    ? searchError.message
                    : 'Could not load products. Try searching again.';
            }
            if (hasSelectionHydrationError) {
                return 'Could not load the selected product. Open the menu to retry.';
            }

            return customNoOptionsMessage
                ? customNoOptionsMessage(input)
                : getDefaultNoOptionsMessage(input);
        },
        [customNoOptionsMessage, hasSelectionHydrationError, searchError]
    );

    const selectedOption = selectedProduct
        ? getProductOptions([selectedProduct], optionsContext.current)[0] || null
        : null;
    const defaultFooterButton = loggedInUser &&
        loggedInUser.permissions.canAccessSetup && {
            label: 'Manage products',
            onClick: () => appHistory.push('/sales/product'),
        };

    return (
        <SelectPickerGeneric
            key={searchContextKey}
            isLoading={isHydratingSelection}
            hasError={Boolean(searchError) || hasSelectionHydrationError}
            inputId={props.inputId}
            isDisabled={props.isDisabled}
            autoFocus={props.autoFocus !== undefined ? props.autoFocus : true}
            openMenuOnFocus={props.openMenuOnFocus}
            defaultMenuIsOpen={
                props.defaultMenuIsOpen !== undefined ? props.defaultMenuIsOpen : true
            }
            placeholder={props.placeholder || 'Search by product, sku, or category…'}
            loadOptions={loadOptions}
            value={selectedOption}
            labelKey='label'
            valueKey='value'
            components={{Option: OptionWithSubtitleAndSublabel}}
            isOptionDisabled={(option: ProductOption) =>
                Boolean(option.isDisabled) || omittedProductIdsSet.has(option.product.id)
            }
            onChange={handleChange}
            onMenuOpen={hasSelectionHydrationError ? retrySelectionHydration : undefined}
            size={props.size}
            hideDivider={props.hideDivider}
            onBlur={props.onBlur}
            onClear={props.onClear}
            getRef={props.getRef}
            footerButton={props.footerButton || defaultFooterButton}
            maxMenuHeight={props.maxMenuHeight}
            noOptionsMessage={noOptionsMessage}
            menuZIndex={props.menuZIndex}
            menuPortalTarget={props.menuPortalTarget}
            menuPlacement={props.menuPlacement}
        />
    );
}
